/**
 * Shared formatting utilities for consistent data display
 * Used across operational and audit views
 */

import { parseDisplayDate } from "@livingsyncs/types";

// calculateAge/getCriticalAlerts/formatRoomInfo/formatResidentName used to be
// re-exported here as thin wrappers around @livingsyncs/ui (frontend's own
// shim, from when this file lived in frontend and those four functions had
// already moved to packages/ui in Phase 2 Task 2.2). Now that formatters.ts
// itself lives in packages/ui too (Phase 5 Task 5.2), the wrapper was a
// same-package self-import — removed; packages/ui's barrel already exports
// those four directly from ./utils/residentFormatters, so nothing is lost,
// just no longer duplicated under two names in the same package.

/*
 * ---------------------------------------------------------------------------
 * Canonical date display
 * ---------------------------------------------------------------------------
 * The resident console showed four spellings of a date in one screenful:
 * "7/13/2026" (move-in), "Jul 27" (timeline), "7/27/2026" (care notes) and
 * "16d ago" (health summary). Three of those are the same kind of fact written
 * three ways, which makes a chart read like it came from three systems.
 *
 * One spelled month, everywhere: "Jul 27, 2026". Numeric M/D/YYYY is dropped
 * because 7/8 is genuinely ambiguous to anyone outside the US — not a
 * theoretical concern in a medical record.
 *
 * "16d ago" stays: it answers a different question (how stale is this reading)
 * and is deliberate — see ResidentHealthSummary.
 */

/**
 * The display parser lives in `@livingsyncs/types` (US-151). It used to be a
 * private copy here, which is why call sites needing a `Date` — a picker bound,
 * for instance — reached for the raw `new Date()` constructor and reintroduced
 * the day-roll: this module exported only string formatters, so there was
 * nothing else to reach for. It is re-exported below.
 */

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const DATE_NO_YEAR_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});
const DATE_NUMERIC_FMT = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  year: "numeric",
});

/** "Jul 27, 2026". Safe for both `@db.Date` strings and full timestamps. */
export function formatDate(
  dateString: string | Date | null | undefined,
): string {
  const d = dateString ? parseDisplayDate(dateString) : null;
  return d ? DATE_FMT.format(d) : "N/A";
}

/**
 * "8/25/2026" — byte-identical to `new Date(x).toLocaleDateString()`, but
 * without the day-roll. This exists so the migration off the raw constructor
 * does not silently restyle the ~107 sites that render dates numerically
 * (US-151 D-2); reach for `formatDate` when you want "Aug 25, 2026".
 */
export function formatDateNumeric(
  dateString: string | Date | null | undefined,
): string {
  const d = dateString ? parseDisplayDate(dateString) : null;
  return d ? DATE_NUMERIC_FMT.format(d) : "N/A";
}

/** "3:42 PM". */
export function formatTime(
  dateString: string | Date | null | undefined,
): string {
  const d = dateString ? parseDisplayDate(dateString) : null;
  return d ? TIME_FMT.format(d) : "N/A";
}

/** "Jul 27, 2026, 3:42 PM". */
export function formatDateTimeDisplay(
  dateString: string | Date | null | undefined,
): string {
  const d = dateString ? parseDisplayDate(dateString) : null;
  return d ? `${DATE_FMT.format(d)}, ${TIME_FMT.format(d)}` : "N/A";
}

/**
 * "Jul 27" for the current year, "Jul 27, 2025" otherwise — for tight columns
 * (timeline rails) where the year is usually noise. The year is NOT dropped
 * unconditionally: an undated "Jul 27" in a 90-day clinical history is exactly
 * the ambiguity this whole cleanup exists to remove.
 */
export function formatDateCompact(
  dateString: string | Date | null | undefined,
): string {
  const d = dateString ? parseDisplayDate(dateString) : null;
  if (!d) return "N/A";
  return d.getFullYear() === new Date().getFullYear()
    ? DATE_NO_YEAR_FMT.format(d)
    : DATE_FMT.format(d);
}

/**
 * Split date/time, for callers that lay the two out separately.
 * Kept as an object for its existing consumers; the strings are canonical.
 */
export function formatDateTime(dateString: string): {
  date: string;
  time: string;
} {
  return { date: formatDate(dateString), time: formatTime(dateString) };
}

/**
 * Severity badge color mapping
 */
export const SEVERITY_COLORS: Record<string, string> = {
  // US-078 (UX-1): low is gray so severity chips are visually distinct from
  // the blue "reported" status chip on the incidents list.
  low: "gray",
  medium: "yellow",
  high: "orange",
  critical: "red",
};

/**
 * Status badge color mapping for incidents
 */
export const INCIDENT_STATUS_COLORS: Record<string, string> = {
  reported: "blue",
  under_review: "yellow",
  resolved: "green",
  escalated: "red",
};

/**
 * Format incident type for display
 */
export function formatIncidentType(incidentType: string): string {
  return incidentType.replace(/_/g, " ");
}

/**
 * Format status for display
 */
export function formatStatus(status: string): string {
  return status.replace(/_/g, " ");
}

const AUDIT_ACTION_PAST_TENSE: Record<string, string> = {
  create: "created",
  update: "updated",
  delete: "deleted",
};

/** Some (resource_type, action) pairs read better as a fixed phrase than the
 * generic "<Resource> <verb-past-tense>" template. `shift` + `create` is the
 * one case: presence-based check-in is the ONLY code path that creates a
 * shift row (there's no more manager-scheduled shift creation), so "Shift
 * created" is stale/confusing terminology from the old scheduling model —
 * "Checked in" is what actually happened. */
const AUDIT_ACTIVITY_LABEL_OVERRIDES: Partial<
  Record<string, Partial<Record<string, string>>>
> = {
  shift: { create: "Checked in" },
};

/**
 * Audit-log activity line, e.g. "Incident updated" / "Incident action
 * created" — resource-first, past-tense, rather than "Update incident".
 */
export function formatAuditActivityLabel(
  action: string,
  resourceType: string,
): string {
  const override = AUDIT_ACTIVITY_LABEL_OVERRIDES[resourceType]?.[action];
  if (override) return override;

  const resourceLabel = resourceType.replace(/_/g, " ");
  const capitalized =
    resourceLabel.charAt(0).toUpperCase() + resourceLabel.slice(1);
  return `${capitalized} ${AUDIT_ACTION_PAST_TENSE[action] ?? action}`;
}

/**
 * Re-exported so a component needing a real `Date` (a Mantine `minDate`/`maxDate`
 * bound, say) has an exported, day-roll-safe way to get one. The absence of this
 * is what made `new Date()` the path of least resistance — US-151.
 */
export { parseDisplayDate } from "@livingsyncs/types";
